import React, { memo, useMemo } from 'react';

import { DiagonalHatchDefinition } from '@da-g3-acl/shared/common';

import { sanitiseId } from '../../../../../helpers/sanitiseId';
import { LengthRange } from '../../../../../types/lengthRange';
import { LoadingGradientDefinition } from '../../svgFills';
import { NumericLaneRowConfiguration, PointsNumericLaneRowDisplayConfiguration } from '../configuration';
import { GreyscaleProvider, useGradients } from '../useGradients';

import styles from './index.module.scss';

export interface PointsNumericLaneRowProps {
	id: string;
	configuration: Omit<NumericLaneRowConfiguration, 'displayConfiguration'>;
	display: PointsNumericLaneRowDisplayConfiguration;
	width: number;
	heightInPx: number;
	scale: number;
	lane: LengthRange;
	position: number;
}

const PointsNumericLaneRowInternal: React.FC<PointsNumericLaneRowProps> = ({
	id,
	configuration,
	display,
	width,
	heightInPx,
	position,
	lane,
	scale,
}) => {
	const { points, isLoading } = configuration;
	const pointsToRender = useMemo(() => {
		return points.filter(({ at }) => at > lane.depthFrom && at < lane.depthTo);
	}, [points, lane]);
	const bounds = configuration.valueBounds;

	const gradientProvider = useMemo(
		() => (display.colourProvider ?? GreyscaleProvider)(bounds),
		[bounds, display.colourProvider],
	);

	const { getColourForRealValue } = useGradients(gradientProvider, bounds);
	return (
		<svg width={width} height={heightInPx} x={0} y={position}>
			<rect key={'background'} className={styles.background} x={0} y={0} width={width} height={heightInPx}></rect>
			{pointsToRender.map((point) => {
				const { at, value } = point;
				const x = (at - lane.depthFrom) * scale;
				const pointScale = display.scaleProvider
					? display.scaleProvider(value, bounds)
					: 1 - (bounds[1] - value) / (bounds[1] - bounds[0]);
				const relativeSize = heightInPx * pointScale;
				const colour = getColourForRealValue(value);
				const sanitizedConfigurationId = sanitiseId(configuration.id);

				switch (display.shape) {
					case null:
					case undefined:
					case 'circle':
						return (
							<circle
								cx={x}
								cy={heightInPx / 2}
								r={relativeSize / 2}
								key={`${id}--${sanitizedConfigurationId}--${at}-point-circle`}
								fill={colour}
								onMouseEnter={() => configuration.onHover?.({ point, colour })}
								onMouseLeave={() => configuration.onHover?.(undefined)}
							></circle>
						);
					case 'bar':
						return (
							<rect
								x={x}
								y={0}
								width={1}
								height={relativeSize}
								key={`${id}--${sanitizedConfigurationId}--${at}-point-bar`}
								fill={colour}
							></rect>
						);
				}
			})}
			<defs>
				<DiagonalHatchDefinition id="diagonalHatch" />
				<LoadingGradientDefinition id="loadingPulseGradient" />
			</defs>
			{isLoading && (
				<>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#loadingPulseGradient)"></rect>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#diagonalHatch)"></rect>
				</>
			)}
		</svg>
	);
};

export const PointsNumericLaneRow = memo(PointsNumericLaneRowInternal);
